/* eslint-disable */
import Card from '@/components/elements/Card/Card';
import Icon from '@/components/icons/Icon';
import { ListItem, ListItemContent } from '@/components/elements/lists';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import { SCREEN_NAME } from '@/constants/analytics';
import { APP_STORE_URL, GOOGLE_PLAY_URL } from '@/constants/downloadApp';
import { SHARE_ROUTE_PATH } from '@/constants/pages';
import { getAppRoutes } from '@/constants/routes';
import { isAndroidPhone, isIPhone, isServer, trackPage, trackMpMetaCustomEvent, url } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { arrayOfStrings, matchRoute } from '@/helpers/utilsTS';
import useMobileView from '@/hooks/useMobileView';
import useScrollToTop from '@/hooks/useScrollToTop';
import { _t } from '@/locale';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';

const getSeo = () => {
  const title = _t('seo.appsDownloadTitle');
  const description = _t('seo.appsDownloadDescription');
  const pageUrl = `${url.getBaseUrl()}app`;
  const imageUrl = url.getBaseImageUrl();

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={pageUrl} />

      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
    </Helmet>
  );
};

const trackPageVisit = () => {
  trackPage();
  trackMpEvent({ name: 'screen_shown', properties: { screen_name: SCREEN_NAME.APP } });
};

const trackClick = (app: 'ios' | 'android') => () => {
  trackMpMetaCustomEvent(`download_${app}_app_clicked`, {});
};

const getInAppRedirectUrl = (isMobileView: boolean) => {
  if (isServer) return null;

  const searchParams = new URLSearchParams(window.location.search);
  let redirectUrl = searchParams.get('link');
  if (redirectUrl && redirectUrl.startsWith(url.getBaseUrl())) {
    try {
      const path = new URL(redirectUrl).pathname;
      if (path) {
        const appRoutes = getAppRoutes(isMobileView);
        const pathSegments = path.split('/').filter(Boolean).length;
        const isShareRoute =
          Boolean(matchRoute({ routePath: SHARE_ROUTE_PATH, exact: true }, path)) &&
          (pathSegments === 3 || pathSegments === 4);
        const isKnownRoute = isShareRoute ||
          appRoutes.some((route) => {
            return matchRoute({ routePath: route.path, exact: route.exact }, path);
          });

        return isKnownRoute ? redirectUrl : null;
      }
    } catch (e) {}
  }

  return null;
};

const showButtons = () => {
  const isIphone = isIPhone();
  const isAndroid = isAndroidPhone();
  const showAll = !isServer && !(isIphone || isAndroid);

  return {
    showAppStore: isIphone || showAll,
    showGooglePlay: isAndroid || showAll,
    showAll,
  };
};

const AppsDownload = () => {
  const { isMobileView } = useMobileView();
  const inAppUrl = useMemo(() => getInAppRedirectUrl(isMobileView), [isMobileView]);
  const { showAppStore, showGooglePlay, showAll } = useMemo(() => showButtons(), []);
  const googlePlayUrl = `${GOOGLE_PLAY_URL}${inAppUrl ? '&url=' + inAppUrl : ''}`;

  useScrollToTop();

  useEffect(() => {
    trackPageVisit();

    if (!isServer) {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('go') && (isIPhone() || isAndroidPhone())) {
        // delay the redirect to allow deep linking logic to kick in
        setTimeout(() => {
          window.location.href = isIPhone() ? APP_STORE_URL : googlePlayUrl;
        }, 100);
      }
    }
  }, [googlePlayUrl]);

  const appsButton = (classname: string) => {
    return (
      <div className={classname}>
        <div className="flex gap-3 md:gap-4">
          {showAppStore && (
            <a href={APP_STORE_URL} onClick={trackClick('ios')}>
              <img src="/images/download-app-store.png" className="h-[40px]" alt={_t('availableAppStore')} />
            </a>
          )}
          {showGooglePlay && (
            <a href={googlePlayUrl} onClick={trackClick('android')}>
              <img src="/images/download-google-play.png" className="h-[40px]" alt={_t('availablePlayStore')} />
            </a>
          )}
        </div>
        {inAppUrl && showAll && (
          <a className="underline" href={inAppUrl}>
            {_t('appsPage.openHere')}
          </a>
        )}
      </div>
    );
  };

  const bulletPoints = arrayOfStrings(_t('appsPage.bulletPoints'));

  return (
    <PageViewLayout type="mainView" wrapperClass="bg-primary-200">
      <div className="p-xl flex justify-center">
        <Card className="flex justify-center md:w-10/12 xl:w-[992px]">
          <div className="gap-lg flex flex-col items-center md:flex-row">
            <picture>
              <source srcSet="/images/appsDownload/download-bokadirekt-apps.png" media="(min-width: 768px)" />
              <img src="/images/appsDownload/download-bokadirekt-apps-mobile.png" alt="" loading="lazy" />
            </picture>
            {appsButton('flex flex-col md:hidden gap-3')}
            <div className="gap-lg flex flex-col">
              <h1 className="text-2xl text-[30px] font-bold md:text-4xl">{_t('appsPage.title')}</h1>
              <h3 className="text-sm">{_t('appsPage.subtitle')}</h3>
              {bulletPoints.length && (
                <ul>
                  {bulletPoints.map((item, i) => (
                    <ListItem
                      key={i}
                      className="py-xs gap-sm"
                      leftPadding={false}
                      leftSlot={<Icon variant="check" color="success-500" />}>
                      <ListItemContent titleClassName="text-black-600 text-base" title={item} />
                    </ListItem>
                  ))}
                </ul>
              )}
              {appsButton('hidden md:flex md:flex-col gap-3')}
            </div>
          </div>
        </Card>
      </div>
      {getSeo()}
    </PageViewLayout>
  );
};

export default AppsDownload;
